
import React from 'react';
import { X, ShieldCheck, Award, Target, Printer, Download, Sparkles, Globe, HeartPulse, Activity } from 'lucide-react';

interface ReadinessCertificateProps {
  user: any;
  activeCert: any;
  saturation: number;
  analytics: any;
  onClose: () => void;
}

export const ReadinessCertificate: React.FC<ReadinessCertificateProps> = ({ user, activeCert, saturation, analytics, onClose }) => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-950/90 backdrop-blur-xl flex items-start justify-center p-4 md:p-10 pt-10 md:pt-16 overflow-y-auto custom-scrollbar no-print">
      <div className="bg-white rounded-[3rem] shadow-[0_40px_100px_rgba(0,0,0,0.5)] w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-pop relative border border-white/20">
        
        {/* Header UI Controls */}
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
          <div className="flex items-center gap-4">
             <div className="p-2 bg-indigo-600 text-white rounded-xl shadow-lg">
                <ShieldCheck size={20} />
             </div>
             <div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight leading-none">Readiness Credentials</h3>
                <p className="text-[10px] font-bold text-slate-400 tracking-widest mt-1.5">Official Verification Portal</p>
             </div>
          </div>
          <div className="flex gap-3">
             <button onClick={handlePrint} className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-black text-[10px] tracking-widest hover:bg-black transition-all shadow-xl active:scale-95 border-b-4 border-black">
                <Printer size={16} /> Print Credentials
             </button>
             <button onClick={onClose} className="p-3 bg-white text-slate-400 rounded-xl hover:text-rose-500 transition-all border border-slate-200 active:scale-90 shadow-sm"><X size={24}/></button>
          </div>
        </div>

        {/* Certificate Surface */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-10 md:p-20 flex justify-center bg-slate-100">
           <div className="bg-white w-full max-w-[800px] aspect-[1/1.41] md:aspect-[1.41/1] p-12 md:p-20 shadow-2xl relative border-[20px] border-slate-50 flex flex-col justify-between overflow-hidden print-area">
              
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-[80px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/5 blur-[80px] rounded-full translate-y-1/2 -translate-x-1/2"></div>
              
              {/* Border accents */}
              <div className="absolute top-4 left-4 right-4 bottom-4 border border-slate-200 pointer-events-none"></div>
              <div className="absolute top-6 left-6 right-6 bottom-6 border-2 border-slate-100 pointer-events-none"></div>

              {/* Certificate Content */}
              <div className="relative z-10 flex flex-col items-center text-center">
                 <div className="mb-10">
                    <img 
                      src="https://static.wixstatic.com/media/cc4206_4525d5f9d45948bd8501990ac76f6c06~mv2.png" 
                      className="h-12 md:h-16 object-contain mx-auto mb-6" 
                      alt="Diamond School Of Nursing" 
                    />
                    <div className="h-1 w-24 bg-indigo-600 mx-auto rounded-full"></div>
                 </div>

                 <p className="text-[10px] md:text-xs font-black tracking-[0.5em] text-slate-400 mb-8">Certificate of Clinical Readiness</p>
                 
                 <h2 className="text-sm md:text-lg font-medium text-slate-500 tracking-widest mb-10">This document verifies the logical saturation of</h2>
                 
                 <h1 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tighter leading-none mb-10 border-b-4 border-slate-900 pb-4 inline-block">
                    {user?.name || 'Authorized Candidate'}
                 </h1>

                 <p className="text-sm md:text-lg font-medium text-slate-500 leading-relaxed max-w-2xl mx-auto mb-12">
                   "Having achieved a registry saturation of <span className="text-slate-900 font-black">{saturation}%</span> and maintaining an average logical accuracy of <span className="text-indigo-600 font-black">{analytics.readiness}%</span>, this candidate is verified as Board-Ready for the {activeCert?.name || 'Healthcare'} certification program."
                 </p>

                 <div className="grid grid-cols-3 gap-8 w-full max-w-xl py-10 border-y border-slate-100">
                    <div className="space-y-1">
                        <p className="text-2xl font-black text-slate-900">{saturation}%</p>
                        <p className="text-[8px] font-black text-slate-400 tracking-widest">Saturation</p>
                    </div>
                    <div className="space-y-1">
                        <p className="text-2xl font-black text-slate-900">{analytics.readiness}%</p>
                        <p className="text-[8px] font-black text-slate-400 tracking-widest">Readiness</p>
                    </div>
                    <div className="space-y-1">
                        <p className="text-2xl font-black text-slate-900">{user?.history?.length || 0}</p>
                        <p className="text-[8px] font-black text-slate-400 tracking-widest">Sessions</p>
                    </div>
                 </div>
              </div>

              <div className="relative z-10 flex justify-between items-end pt-12">
                 <div className="text-left space-y-4">
                    <div className="space-y-1">
                        <p className="text-[10px] font-black text-slate-900 tabular-nums">{new Date().toLocaleDateString()}</p>
                        <div className="h-px w-32 bg-slate-300"></div>
                        <p className="text-[8px] font-black text-slate-400 tracking-widest">Verification Date</p>
                    </div>
                 </div>

                 {/* Gold Logic Seal */}
                 <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center group">
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 rounded-full shadow-2xl animate-spin-slow p-1">
                        <div className="w-full h-full border-[6px] border-white/30 rounded-full border-dashed"></div>
                    </div>
                    <div className="relative z-10 flex flex-col items-center gap-1">
                        <Award size={48} className="text-white drop-shadow-lg" />
                        <span className="text-[10px] font-black text-white tracking-tighter">Verified Logic</span>
                    </div>
                 </div>

                 <div className="text-right space-y-4">
                    <div className="space-y-1">
                        <p className="text-[10px] font-black text-indigo-600 tracking-tight">Logic Engine GA-8</p>
                        <div className="h-px w-32 bg-slate-300 ml-auto"></div>
                        <p className="text-[8px] font-black text-slate-400 tracking-widest">Registry Authority</p>
                    </div>
                 </div>
              </div>

              {/* Security Trace */}
              <div className="absolute bottom-6 left-0 right-0 flex justify-center opacity-20">
                 <p className="text-[7px] font-mono tracking-[0.5em] text-slate-400">
                    CERTID: {activeCert?.id || 'GLB'}-{user?.id?.slice(-8) || '00000000'} // AUTH: REGISTRY-SECURE
                 </p>
              </div>
           </div>
        </div>
      </div>

      <style>{`
        @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
            animation: spin-slow 15s linear infinite;
        }
        @media print {
          body * { visibility: hidden !important; }
          .print-area, .print-area * { visibility: visible !important; }
          .print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 2cm !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};
